import { BucketSummaryEnum } from "constants/index";
import {
  StrategyReportSingleValueCardDescriptions,
  StrategyReportSingleValueCardLabels,
} from "helpers/constants";

/**
 * The footers the Summary section's three value tiles carry, for the scope
 * the summary switch is on.
 *
 * Every scope but Net Value describes the tile itself -- "Beginning
 * balance", "Ending balance", "Absolute change". Net Value's three figures
 * are the same three with the account's margin balance folded into them
 * (bucket_report._net_value_cards, operator task 3543), so all three say so
 * instead: a reader comparing Net Value against Long Value sees three
 * figures that do not reconcile, and the footer is where the page explains
 * why.
 *
 * It is a MAPPING in its own module rather than three ternaries inline for
 * the reason the repo keeps reaching for: the rule is "these three tiles,
 * this one scope", which is a single statement that can be pinned by a test
 * and read in one place -- and inline it would live in two, because the
 * live cards and the PDF payload each render these footers separately and
 * must not drift apart.
 */
export const summaryValueCardFooters = (
  summary: BucketSummaryEnum,
): {
  startingValue: string;
  endingValue: string;
  netChange: string;
} => {
  if (summary === BucketSummaryEnum.NET_VALUE) {
    const adjusted =
      StrategyReportSingleValueCardDescriptions.ADJUSTED_FOR_MARGIN_BALANCE;
    return {
      startingValue: adjusted,
      endingValue: adjusted,
      netChange: adjusted,
    };
  }
  return {
    startingValue: StrategyReportSingleValueCardDescriptions.STARTING_VALUE,
    endingValue: StrategyReportSingleValueCardDescriptions.ENDING_VALUE,
    netChange: StrategyReportSingleValueCardDescriptions.NET_CHANGE,
  };
};

// WHICH tiles Net Value dims, and the whole of that membership. A Set of
// the cards' OWN labels rather than a second vocabulary to keep in step:
// the tile passes the label it already renders, so a tile that is added,
// renamed or removed reaches this mapping as itself.
const DIMMED_ON_NET_VALUE: ReadonlySet<StrategyReportSingleValueCardLabels> =
  new Set([
    StrategyReportSingleValueCardLabels.INCOME_RECEIVED,
    StrategyReportSingleValueCardLabels.MARGIN_INTEREST,
  ]);

/**
 * Whether ONE named Summary tile is de-emphasised for the scope the summary
 * switch is on (operator, task 3580; narrowed from three tiles to two, and
 * re-keyed from the scope alone to the scope AND the tile, by task 3637).
 *
 * **It takes the tile because the answer now differs BETWEEN tiles**, where
 * a bare `=== NET_VALUE` could only say "this scope dims something" and left
 * WHICH tiles in the JSX of a two-thousand-line component, out of reach of
 * any test here. That was survivable while all three of the non-value tiles
 * answered alike; task 3637 split them, and a split the caller holds is a
 * split nothing can pin. Passing each tile's own label through the mapping
 * makes the membership the module's again: a caller that hands the return
 * tile this predicate gets `false` and leaves it at full strength, so the
 * tile it used to dim cannot be re-dimmed by an edit at the call site.
 *
 * It is the COMPLEMENT of the mapping above, and it lives beside it for that
 * reason rather than in a module of its own. Both answer one question --
 * which of the six tiles Net Value actually changes -- and the two answers
 * have to stay each other's opposite. Split across two files they are two
 * copies of the same `=== NET_VALUE` condition, and the day a tile starts
 * responding to the scope one of them is updated and the other is not: the
 * page would then fold margin into a tile it was still dimming as unaffected.
 * That day has since arrived, which is the argument's own best evidence --
 * task 3637 moved `card_4` out of this set, and because both halves are in
 * front of the same reader it moved as one edit.
 *
 * Only Net Value dims anything. Its three value tiles carry the account's
 * margin balance (above), and the two this covers do not move with it at all:
 * `card_5` is the period's income and `card_6` the account's accrued margin
 * interest, both of which the backend reports identically for Long Value and
 * Net Value.
 *
 * `card_4`, the scope's time-weighted return, is deliberately NOT one of them
 * any more. It was, and for a good reason: the backend refused the return
 * outright on a combined scope, so the tile stood there holding a figure that
 * was not the scope's at all, and dimming it was the only honest thing the
 * page could do with it. Task 3637 changed the premise rather than the
 * presentation -- Net Value's `card_4` is now a figure in its own right, the
 * balance-adjusted net value chained day over day -- so the return tile is
 * answering precisely the question the reader asked by reaching for the
 * switch, and dimming it would recede the one figure the switch was for.
 *
 * So on Net Value these two are the only tiles on screen that are NOT
 * answering that question, and dimming them is what stops two unchanged
 * figures reading as part of the margin-adjusted answer beside them.
 *
 * Every other scope leaves all six at full strength, because on a numbered
 * bucket all six describe that bucket and on Long Value all six describe the
 * portfolio those buckets add up to -- where each figure comes from is
 * `longValueCards`' business and not this one's.
 */
export const summaryTileDimmed = (
  summary: BucketSummaryEnum,
  tile: StrategyReportSingleValueCardLabels,
): boolean =>
  summary === BucketSummaryEnum.NET_VALUE && DIMMED_ON_NET_VALUE.has(tile);

/**
 * What one Summary tile shows for the scope the switch is on, when what it
 * shows is not simply its figure (operator, task 3639).
 *
 * The predicate above answers whether a tile RECEDES; this answers whether it
 * has a figure to recede at all. Two questions rather than one, because task
 * 3639 gave Income Received scopes on which there is nothing to show: dimming
 * says "this is not part of the answer", and a tile that is merely dimmed
 * still states a figure the reader can act on.
 *
 * `placeholder` and `unavailableReason` are the two ways to have no figure and
 * they are NOT interchangeable -- reportFieldStates is the single home of that
 * distinction and this type only carries it. A placeholder says no calculation
 * exists ("NOT IMPLEMENTED", printed verbatim); a reason says the page reached
 * the figure and is withholding it, which renders N/A with the sentence on the
 * tile's info icon. At most one of the two is ever set.
 */
export interface SummaryTileState {
  dimmed: boolean;
  placeholder: string | null;
  unavailableReason?: string;
  /**
   * The tile's footer, when this scope's figure needs a different one from the
   * tile's standing description. Absent means the standing one.
   */
  footer?: string;
  /**
   * The tile takes the reader's own figure even while it is dimmed (operator,
   * task 3781). Net Value's two withheld tiles set it: they recede and read
   * N/A until someone types a figure, and a figure typed there is shown at
   * full strength (see `netValueCustomTile`). Absent means a dimmed tile is
   * read-only, which is every other dimmed state on the row.
   */
  overridable?: boolean;
}

/**
 * Why Net Value's Income Received is withheld rather than shown.
 *
 * Written by task 3639, when Bucket 3 and Long Value were withheld for the
 * same cause. Task 3790 restored those two scopes' figures and the operator
 * chose to leave Net Value -- this sentence included -- exactly as it was.
 */
const COMBINED_SCOPE_INCOME_NOT_IMPLEMENTED =
  "Income Received is not implemented for Bucket 3 yet, so the combined " +
  "scopes cannot state the portfolio's income.";

/** Why Net Value's Margin Interest is withheld (operator, task 3639). */
const NET_VALUE_MARGIN_INTEREST_WITHHELD =
  "This scope's figures are adjusted for the account's margin balance; the " +
  "accrued margin interest is not part of that adjustment and is not shown " +
  "here.";

/**
 * One tile's state, with the dimming READ OFF `summaryTileDimmed` rather than
 * decided again here.
 *
 * That composition is the point: `DIMMED_ON_NET_VALUE` stays the single home
 * of which tiles Net Value de-emphasises, and this adds the one case that
 * membership cannot express -- a tile with no figure at all is dimmed on its
 * own account, whatever the scope thinks of it, because a placeholder left at
 * full figure weight reads as a value (reportFieldStates).
 */
/**
 * A Net Value tile the reader has typed their own figure into (operator,
 * task 3781). The withholding is the page's call about the BACKEND's figure;
 * a figure the reader supplied is theirs to state, so it is shown like any
 * other -- undimmed, with no N/A reason -- and prints on paper the same way.
 * It stays overridable so the reader can change it again, or empty the box
 * to go back to the withheld N/A.
 */
const netValueCustomTile = (): SummaryTileState => ({
  placeholder: null,
  dimmed: false,
  overridable: true,
});

const tileState = (
  summary: BucketSummaryEnum,
  tile: StrategyReportSingleValueCardLabels,
  withheld: Omit<SummaryTileState, "dimmed"> = { placeholder: null },
): SummaryTileState => ({
  ...withheld,
  dimmed:
    summaryTileDimmed(summary, tile) ||
    withheld.placeholder !== null ||
    Boolean(withheld.unavailableReason),
});

/**
 * The Income Received tile's state for one scope (operator, task 3639).
 *
 *   - **Bucket 1, Bucket 2, Bucket 3 and Long Value** show their own figure.
 *     What that figure IS differs -- Bucket 1's is its dividends alone,
 *     Bucket 2's its dividends plus its option premium (the backend's
 *     `pair_bucket_income`), Bucket 3's the dividends its securities paid in
 *     the period, and Long Value's the backend `long_value` entry's own
 *     figure (task 4700; task 3790 restored the last two, which 3639 had
 *     seeded NOT IMPLEMENTED) --
 *     and Bucket 1 and Bucket 3 carry their own FOOTER for that reason. The
 *     tile's standing footer names dividends and option premium together,
 *     which is false of a dividends-only figure, and a currency card that a
 *     client reads must not caption a figure with a term it excludes.
 *   - **Net Value** is withheld with a REASON rather than seeded -- the
 *     operator asked for the tile to carry no figure here, and kept it so
 *     when task 3790 restored the other scopes; the sentence is what stops
 *     that reading as a bare, unexplained N/A on a scope whose whole point is
 *     that its figures are adjusted.
 */
export const summaryIncomeTile = (
  summary: BucketSummaryEnum,
  // Whether the reader has typed their own figure into this tile on the
  // scope now selected. Only Net Value reads it (operator, task 3781).
  custom = false,
): SummaryTileState => {
  const tile = StrategyReportSingleValueCardLabels.INCOME_RECEIVED;
  if (
    summary === BucketSummaryEnum.BUCKET_1 ||
    summary === BucketSummaryEnum.BUCKET_3
  ) {
    return tileState(summary, tile, {
      placeholder: null,
      footer:
        StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED_DIVIDENDS_ONLY,
    });
  }
  if (summary === BucketSummaryEnum.NET_VALUE) {
    if (custom) return netValueCustomTile();
    return tileState(summary, tile, {
      placeholder: null,
      unavailableReason: COMBINED_SCOPE_INCOME_NOT_IMPLEMENTED,
      overridable: true,
    });
  }
  return tileState(summary, tile);
};

/**
 * The Margin Interest tile's state for one scope (operator, task 3639).
 *
 * Net Value alone withholds it, and every other scope -- the two numbered
 * buckets, Bucket 3 and Long Value -- shows the figure it always has. Margin
 * interest is charged to an ACCOUNT rather than to a bucket, so those four
 * scopes all have a real charge to report; Net Value is the one scope whose
 * question is about the margin BALANCE, and the accrued interest beside it is
 * a different quantity that the adjustment does not touch.
 *
 * It is NEVER seeded. "NOT IMPLEMENTED" would be a false claim: the backend
 * calculates this figure for every scope including this one.
 */
export const summaryMarginInterestTile = (
  summary: BucketSummaryEnum,
  // As summaryIncomeTile's: only Net Value reads it (operator, task 3781).
  custom = false,
): SummaryTileState => {
  const tile = StrategyReportSingleValueCardLabels.MARGIN_INTEREST;
  if (summary !== BucketSummaryEnum.NET_VALUE) return tileState(summary, tile);
  if (custom) return netValueCustomTile();
  return tileState(summary, tile, {
    placeholder: null,
    unavailableReason: NET_VALUE_MARGIN_INTEREST_WITHHELD,
    overridable: true,
  });
};

/**
 * A Summary tile on the Long Value scope, given the reason (if any) the run
 * could not give that scope its backend figures (operator, task 4700).
 *
 * Income Received and Margin Interest are read off the `long_value` entry
 * alone, so when it is missing they withhold their figure with the same
 * sentence the three value cards carry -- the screen shows N/A beside it and
 * the PDF prints "N/A" off the same pair. Not dimmed, so the five cards
 * withheld for the one reason look alike. Every other scope, and a Long Value
 * run that did carry the entry, passes through untouched.
 */
export const withLongValueReason = (
  state: SummaryTileState,
  summary: BucketSummaryEnum,
  reason: string | undefined,
): SummaryTileState =>
  summary === BucketSummaryEnum.LONG_VALUE && reason
    ? { ...state, unavailableReason: reason }
    : state;

/**
 * What a tile in one of the states above actually renders, given the figure it
 * would otherwise have shown.
 *
 * The substitution is stated ONCE here because it is made TWICE -- the live
 * card and the PDF payload each render these tiles separately, exactly as they
 * do the footers above, and a paper copy printing a figure the screen withheld
 * is the one drift this module exists to prevent. A withheld figure becomes
 * the EMPTY SENTINEL rather than being passed through: the card reads N/A off
 * the reason either way, but the export takes the value, and `pdfFigureValue`
 * turns a reason into "N/A" on paper from that same pair.
 */
export const summaryTileValue = (
  state: SummaryTileState,
  figure: number | string,
): number | string => {
  if (state.placeholder !== null) return state.placeholder;
  return state.unavailableReason ? "" : figure;
};
